import { useState } from "react";
import api from "../config/api";

/**
 * Custom hook for deleting guest images
 *
 * Features:
 * - Deletes guest image by ID via API
 * - Manages loading, error, and success states
 * - Provides error handling and user feedback
 *
 * @returns {Object} - Hook state and functions
 */
const useDeleteGuestImage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  /**
   * Deletes a guest image by ID
   * 
   * @param {number} imageId - ID of the image to delete
   * @param {number} eventId - Event ID (for API endpoint)
   * @returns {Promise<boolean>} - True if successful, false if error
   */
  const deleteGuestImage = async (imageId, eventId) => {
    if (!imageId) {
      setError("Image ID is required");
      return false;
    }

    if (!eventId) {
      setError("Event ID is required");
      return false;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await api.delete(`/event-guest-images/images/${imageId}`);

      if (response.status === 200 || response.status === 204) {
        setSuccess(true);
        return true;
      } else {
        setError("Failed to delete image");
        return false;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete image";
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resets the hook state
   */
  const resetState = () => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  };

  return {
    deleteGuestImage,
    loading,
    error,
    success,
    resetState,
  };
};

export default useDeleteGuestImage;
